<!--
  Модалка «Оформить приход/расход» для страницы службы.
  По ТЗ 10.09.2026: расход обязательно требует подтип
  (списание / передача выше / возврат выше).
-->
<template>
  <div class="ofm__backdrop" @mousedown.self="$emit('close')">
    <div class="ofm" role="dialog" @mousedown.stop>
      <header class="ofm__head">
        <h3>{{ kind === 'receipt' ? $t('household.service.addReceipt') : $t('household.service.addExpense') }}</h3>
        <button class="ofm__close" @click="$emit('close')" aria-label="×">×</button>
      </header>

      <div class="ofm__body">
        <div v-if="error" class="ofm__err">{{ error }}</div>

        <label class="ofm__field">
          <span>{{ $t('household.service.formDate') }}</span>
          <input type="date" v-model="form.date" />
        </label>

        <label class="ofm__field">
          <span>{{ $t('household.service.formNomenclature') }}</span>
          <select v-model="form.nomenclature">
            <option :value="null">— {{ $t('common.select') }} —</option>
            <option v-for="n in nomenclature" :key="n.id" :value="n.id">
              {{ n.name }} ({{ n.unit }})
            </option>
          </select>
        </label>

        <label class="ofm__field">
          <span>{{ $t('household.service.formQuantity') }}</span>
          <input type="number" min="0" step="0.01" v-model.number="form.quantity" />
        </label>

        <label class="ofm__field">
          <span>{{ $t('household.service.formCost') }}</span>
          <input type="number" min="0" step="0.01" v-model.number="form.cost" />
        </label>

        <label class="ofm__field" v-if="kind === 'expense'">
          <span>{{ $t('household.service.formExpenseSubtype') }}</span>
          <select v-model="form.expense_subtype">
            <option value="write_off">{{ $t('household.service.subtypeWriteOff') }}</option>
            <option value="transfer_up">{{ $t('household.service.subtypeTransferUp') }}</option>
            <option value="return_up">{{ $t('household.service.subtypeReturnUp') }}</option>
          </select>
        </label>

        <label class="ofm__field">
          <span>{{ $t('household.service.formOrderRef') }}</span>
          <input type="text" v-model="form.order_ref" :placeholder="$t('household.service.formOrderRefPh')" />
        </label>

        <label class="ofm__field ofm__field--wide">
          <span>{{ $t('household.service.formComment') }}</span>
          <textarea rows="2" v-model="form.description"></textarea>
        </label>
      </div>

      <footer class="ofm__foot">
        <button class="btn btn--secondary" @click="$emit('close')">{{ $t('common.cancel') }}</button>
        <button class="btn btn--primary" :disabled="saving" @click="save">
          {{ saving ? $t('common.saving') : (kind === 'receipt' ? $t('household.service.doReceipt') : $t('household.service.doExpense')) }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { createOperation, listNomenclature } from '@/api/wmsService'

export default {
  name: 'OperationFormModal',
  props: {
    service: { type: Object, default: null },
    kind: { type: String, required: true }, // 'receipt' | 'expense'
    // По ТЗ 10.09.2026: склад известен из контекста страницы службы.
    warehouseId: { type: [Number, String], default: null },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      saving: false, error: null,
      nomenclature: [],
      form: {
        date: this.todayISO(),
        nomenclature: null,
        quantity: 1,
        cost: 0,
        expense_subtype: 'write_off',
        order_ref: '',
        description: '',
      },
    }
  },
  async mounted() {
    try {
      this.nomenclature = await listNomenclature()
    } catch (e) { console.error(e) }
  },
  methods: {
    todayISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` },
    async save() {
      if (!this.warehouseId || !this.form.nomenclature || !this.form.quantity) {
        this.error = this.$t('common.fillRequired'); return
      }
      this.saving = true; this.error = null
      try {
        const payload = {
          date: this.form.date,
          warehouse: this.warehouseId,
          operation_type: this.kind === 'receipt' ? 'add' : 'expense',
          expense_subtype: this.kind === 'expense' ? this.form.expense_subtype : '',
          order_ref: this.form.order_ref || '',
          description: this.form.description || '',
          nomenclatures: [{
            nomenclature: this.form.nomenclature,
            quantity: this.form.quantity,
            cost: this.form.cost || 0,
            batch: '',
          }],
        }
        await createOperation(payload)
        this.$emit('saved')
      } catch (e) {
        this.error = e?.response?.data ? JSON.stringify(e.response.data) : (e.message || 'Save failed')
      } finally { this.saving = false }
    },
  },
}
</script>

<style scoped>
.ofm__backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.ofm { background: #fff; border-radius: 12px; width: min(600px, 96vw); max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,.25); overflow: hidden; }
.ofm__head { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; border-bottom: 1px solid #E5E7EB; }
.ofm__head h3 { margin: 0; font-size: 17px; }
.ofm__close { background: none; border: 0; font-size: 24px; cursor: pointer; color: #6B7280; }
.ofm__body { padding: 16px 20px; overflow: auto; display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
.ofm__field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #374151; }
.ofm__field--wide { grid-column: 1 / -1; }
.ofm__field > span { font-weight: 600; color: #6B7280; }
.ofm__field input, .ofm__field select, .ofm__field textarea {
  padding: 8px 10px; border: 1px solid #D1D5DB; border-radius: 8px; font-family: inherit; font-size: 13px;
}
.ofm__err { grid-column: 1 / -1; color: #B91C1C; font-size: 13px; padding: 8px; background: #FEE2E2; border-radius: 6px; }
.ofm__foot { display: flex; justify-content: flex-end; gap: 10px; padding: 12px 20px; border-top: 1px solid #E5E7EB; }
.btn { padding: 8px 16px; border-radius: 8px; border: 1px solid transparent; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; }
.btn--primary { background: #2F78EE; color: #fff; }
.btn--primary:disabled { opacity: .5; }
.btn--secondary { background: #fff; border-color: #D1D5DB; color: #374151; }
</style>
